import {Component, DestroyRef, inject} from '@angular/core';
import {FormMode} from '../../../shared/types/common';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ManagementService} from '../management.service';
import {StateService} from '../../../services/state.service';
import {CustomValidators} from '../../../shared/validators';
import {objectArrayToSelectOptions} from '../../../utils/mappings';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AccountPayload, AccountUsersResponse, BrandSummary} from '../types';
import {TitledInputComponent} from '../../../shared/components/titled-input/titled-input.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {UtilService} from '../../../services/util.service';
import {ResultData} from '../../../shared/components/search-result-list/search-result-list.component.types';
import {SelectItem, SelectMenuComponent, SelectMode} from '../../../shared/components/select-menu/select-menu.component';
import {CommonModule} from '@angular/common';
import {toSnakeCase} from '../../../utils/strings';

@Component({
  selector: 'app-account-modal',
  imports: [
    ReactiveFormsModule,
    TitledInputComponent,
    ButtonComponent,
    SelectMenuComponent,
    CommonModule
  ],
  templateUrl: './account-modal.component.html',
  styleUrl: './account-modal.component.scss'
})
export class AccountModalComponent {

  public currentMode: FormMode = FormMode.ADD;
  form: FormGroup<{
    name: FormControl<string | null>;
    key: FormControl<string | null>;
    brands: FormControl<string[] | null>;
  }>;
  public configs = {
    [FormMode.ADD]: {
      title: 'Add Account',
      submitText: 'Add',
      submitFunction: () => this.addAccount()
    },
    [FormMode.EDIT]: {
      title: 'Edit Account',
      submitText: 'Update',
      submitFunction: () => this.editAccount()
    }
  };
  public accountUsers?: string[];
  public brandOptions: SelectItem[] = [];
  public data = inject(MAT_DIALOG_DATA);
  public redirectCallback = (result: ResultData) => {
    window.open(`https://${result.value}`, '_blank', 'noopener');
  }
  protected readonly FormMode = FormMode;
  protected readonly SelectMode = SelectMode;
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  constructor(private dialogRef: MatDialogRef<AccountModalComponent>, private managementService: ManagementService,
              private stateService: StateService, private utilService: UtilService) {
    this.form = this.fb.group({
      key: new FormControl({
        value: '',
        disabled: !!this.data
      }, [CustomValidators.required('Account key'), CustomValidators.brandKey('Account key')]),
      name: new FormControl('', {validators:[CustomValidators.required('Account name')], updateOn: 'blur'}),
      brands: new FormControl<string[]>([]),
    });
    if (this.data){
      this.currentMode = FormMode.EDIT;
      this.managementService.getAccountUsers(this.data.accountKey)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((users) => {
          this.accountUsers = this.userResponseToStringArray(users);
        });
      this.form.patchValue({
        ...this.data,
        key: this.data.accountKey,
        brands: this.data.brands.map((brand: BrandSummary) => brand.brandKey)
      });
    }
    this.managementService.brandsList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((brands) => {
        this.brandOptions = objectArrayToSelectOptions(brands, "brandKey", "name");
      });
    this.form.get('name')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
      if (!this.data && value) {
        const snake = toSnakeCase(value);
        this.form.get('key')?.setValue(snake, { emitEvent: false });
      }
    });

  }

  get key(): FormControl {
    return this.form.get('key') as FormControl;
  }

  get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  get brands(): FormControl<string[]> {
    return this.form.get('brands') as FormControl<string[]>;
  }

  submit() {
    this.stateService.loading = true;
    this.configs[this.currentMode].submitFunction();
  }

  cancel() {
    this.dialogRef.close();
  }

  addAccount() {
    this.managementService.addAccount(this.getAccountPayload()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            this.managementService.fetchAndStoreAccounts();
            this.dialogRef.close(result)
          },
          error: error => {
            this.stateService.loading = false;
            this.utilService.openSnackBar(error.error.message, {variant: 'error', position: 'center'}
            )
          }
        }
      )
  }

  editAccount() {
    this.managementService.editAccount(this.getAccountPayload()).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: result => {
            this.dialogRef.close(result)
          },
          error: error => {
            this.stateService.loading = false;
            this.utilService.openSnackBar(error.error.message, {variant: 'error', position: 'center'})
          }
        }
      );
  }

  private userResponseToStringArray(users: AccountUsersResponse): string[] {
    return users.accountUsers.map(user => `${user.firstName} ${user.lastName} (${user.email})`);
  }

  private getAccountPayload() {
    const form = this.form.getRawValue();
    return {
      accountKey: form.key,
      name: form.name,
      brandKeys: form.brands,
    } as AccountPayload;
  }

}
